import AnimatedText from '../../components/component.animatedText';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Get } from '../../services/service.api';
import FullScreenLoader from '../../components/FullScreenLoader';
import {
  TrendingUp, ShoppingCart, DollarSign,
  Megaphone, Users, Package, ArrowUp, ArrowDown,
  BarChart2, RefreshCw, AlertTriangle, CheckCircle2,
  Calendar, RotateCcw, Scale
} from 'lucide-react';

// Formatage des nombres en FCFA
function formatFCFA(n) {
  if (!n && n !== 0) return '0 FCFA';
  return `${Number(n).toLocaleString('fr-FR')} FCFA`;
}

function KpiCard({ icon: Icon, label, value, sub, badge, color, bgColor }) {
  return (
    <div className="kpi-card" style={{ '--kpi-color': color, '--kpi-bg': bgColor }}>
      <div className="kpi-header">
        <div className="kpi-icon">
          <Icon size={20} />
        </div>
        {badge !== undefined && (
          <span className={`kpi-badge ${badge >= 0 ? 'positive' : 'negative'}`}>
            {badge >= 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
            {Math.abs(badge)}%
          </span>
        )}
      </div>
      <div>
        <div className="kpi-value number-format"><AnimatedText text={value} /></div>
        <div className="kpi-label">{label}</div>
        {sub && <div className="kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

function MiniBarChart({ data, label, color = 'var(--primary)' }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.total), 1);

  function shortFCFA(n) {
    if (n === 0) return '0';
    if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(0)}k`;
    return String(n);
  }

  return (
    <div className="chart-card">
      <div className="chart-header">
        <span className="chart-title">{label}</span>
        <BarChart2 size={16} style={{ color: 'var(--text-muted)' }} />
      </div>
      <div className="bar-chart" style={{ alignItems: 'flex-end', paddingBottom: 24, position: 'relative' }}>
        {data.map((d, i) => {
          const height = max > 0 ? Math.max((d.total / max) * 100, 6) : 6;
          const dateStr = d.date ? d.date.slice(5) : '';
          return (
            <div
              key={i}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, height: '100%', justifyContent: 'flex-end', position: 'relative' }}
              title={`${d.date}: ${formatFCFA(d.total)}`}
            >
              {d.total > 0 && (
                <span style={{
                  fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: 2,
                  fontWeight: 600, whiteSpace: 'nowrap'
                }}>
                  {shortFCFA(d.total)}
                </span>
              )}
              <div
                className="bar"
                style={{ flex: 'none', height: `${height}%`, background: color, width: '100%', borderRadius: '3px 3px 0 0', minHeight: 4 }}
              />
              <span className="bar-label" style={{ position: 'absolute', bottom: -20, fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                {dateStr}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const isInvestor = user?.role === 'investisseur';

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtres par date et campagne
  const [dateRangePreset, setDateRangePreset] = useState('all'); // 'all', '7d', '30d', 'month', 'year', 'custom'
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [campagneId, setCampagneId] = useState('all');

  // Charger les campagnes pour le filtre lorsque l'entreprise active change
  
  useEffect(() => {
    setStats(null); // Clear stats when company changes to prevent ghost data
    setCampagneId('all'); // Reset campaign ID immediately
    if (!token) return;
    Get('/campaigns', token).then(res => {

      if (Array.isArray(res)) {
        const sorted = res.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setCampaigns(sorted);
        
        const storageKey = activeCompany?.id ? `dashboard_campagne_id_${activeCompany.id}` : 'dashboard_campagne_id';
        const savedCampagneId = localStorage.getItem(storageKey);
        
        if (savedCampagneId && (savedCampagneId === 'all' || sorted.some(c => c.id === savedCampagneId))) {
          setCampagneId(savedCampagneId);
        } else if (sorted.length > 0) {
          const defaultCampagneId = sorted[0].id;
          setCampagneId(defaultCampagneId);
          localStorage.setItem(storageKey, defaultCampagneId);
        } else {
          setCampagneId('all');
        }
      } else {
        setCampaigns([]);
        setCampagneId('all');
      }
    });
  }, [token, activeCompany?.id]);

  // Fonction pour charger les stats avec les filtres par date et campagne
  
  const abortControllerRef = useRef(null);

  const fetchStats = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      if (campagneId && campagneId !== 'all') params.append('campagne_id', campagneId);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const companyId = activeCompany?.id || '';
      const headers = { authorization: "Bearer " + token, 'Content-Type': 'application/json' };
      if (companyId) headers['X-Company-Id'] = companyId;
      
      const API_URL = process.env.REACT_APP_API_URL || "https://savaninvests-backend.onrender.com";
      const response = await fetch(`${API_URL}/stats${queryString}`, { headers, signal });
      
      if (!response.ok && response.status === 401) {
         // handle unauthorized implicitly or let interceptor handle
      }
      
      const res = await response.json();

      if (!signal.aborted && res && !res.error) {
        setStats(res);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // silently fail
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [token, dateDebut, dateFin, campagneId, activeCompany?.id]);


  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Écouter l'événement global de changement d'entreprise
  

  const handleCampagneChange = (e) => {
    const val = e.target.value;
    setCampagneId(val);
    const storageKey = activeCompany?.id ? `dashboard_campagne_id_${activeCompany.id}` : 'dashboard_campagne_id';
    localStorage.setItem(storageKey, val);
  };

  // Gestion des boutons rapides de période
  const handlePresetChange = (preset) => {
    setDateRangePreset(preset);
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];

    if (preset === 'all') {
      setDateDebut('');
      setDateFin('');
    } else if (preset === '7d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      setDateDebut(d.toISOString().split('T')[0]);
      setDateFin(todayStr);
    } else if (preset === '30d') {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      setDateDebut(d.toISOString().split('T')[0]);
      setDateFin(todayStr);
    } else if (preset === 'month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setDateDebut(firstDay.toISOString().split('T')[0]);
      setDateFin(todayStr);
    } else if (preset === 'year') {
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
      setDateDebut(firstDayOfYear.toISOString().split('T')[0]);
      setDateFin(todayStr);
    }
  };

  const handleResetFilters = () => {
    setDateRangePreset('all');
    setDateDebut('');
    setDateFin('');
    const storageKey = activeCompany?.id ? `dashboard_campagne_id_${activeCompany.id}` : 'dashboard_campagne_id';
    if (campaigns.length > 0) {
      setCampagneId(campaigns[0].id);
      localStorage.setItem(storageKey, campaigns[0].id);
    } else {
      setCampagneId('all');
      localStorage.setItem(storageKey, 'all');
    }
  };

  const isFilterActive = dateDebut !== '' || dateFin !== '' || (campaigns.length > 0 && campagneId !== campaigns[0].id);

  // Données financières calculées
  const totalVentes = stats?.ventes?.total_fcfa ?? stats?.totalVentesFCFA ?? 0;
  const totalAchats = stats?.achats?.total_fcfa ?? stats?.totalAchatsFCFA ?? 0;
  const totalInvest = stats?.investissements?.total_fcfa ?? stats?.totalInvestFCFA ?? 0;

  // 1. Revient (Ventes - Achats)
  const revientValeur = stats?.revient?.valeur ?? (totalVentes - totalAchats);
  const isRevientPositif = revientValeur >= 0;
  const margePercent = totalVentes > 0 ? ((revientValeur / totalVentes) * 100).toFixed(1) : null;

  // 2. Différence (Investissements - Achats)
  const diffInvestAchatsValeur = stats?.difference_invest_achats?.valeur ?? (totalInvest - totalAchats);
  const isDiffInvestPositif = diffInvestAchatsValeur >= 0;

  // 3. Bénéfice net global
  const benefice = stats?.benefice?.valeur ?? (totalVentes - totalInvest);
  const isProfit = benefice >= 0;

  // 4. Revenus personnels
  const myPercentage = stats?.revenus_personnels?.percentage ?? 0;
  const myIncome = stats?.revenus_personnels?.valeur ?? 0;

  // 5. Total pourcentage alloué
  const totalPercentAlloue = stats?.total_percentage_alloue ?? 0;

  // 6. Bénéfice par campagne
  const beneficeParCampagne = stats?.benefice_par_campagne ?? [];

  // Alertes de stock : uniquement les produits épuisés (quantite === 0)
  const outOfStockItems = stats?.stock?.filter(p => p.quantite === 0) || [];

  return (
    <div className="page-content page-enter">
      {loading && <FullScreenLoader message="Chargement des statistiques..." />}
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>{t.dashboard.title}</h1>
          <p>
            {t.common.hello}, <strong>{user?.prenom || user?.nom || 'utilisateur'}</strong> 👋 &nbsp;•&nbsp; {t.dashboard.subtitle}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline btn-sm" onClick={fetchStats} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Barre de Filtres Interactifs (Dates) */}
      <div className="dashboard-filter-bar">
        {/* Filtres par Période & Dates */}
        <div className="filter-group-wrapper">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 600 }}>
            <Calendar size={16} style={{ color: 'var(--gold)' }} />
            <span>{t.dashboard.filterByDate} :</span>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`filter-pill-btn ${dateRangePreset === 'all' ? 'active' : ''}`}
              onClick={() => handlePresetChange('all')}
            >
              {t.dashboard.periodAll}
            </button>
            <button
              type="button"
              className={`filter-pill-btn ${dateRangePreset === '7d' ? 'active' : ''}`}
              onClick={() => handlePresetChange('7d')}
            >
              {t.dashboard.period7d}
            </button>
            <button
              type="button"
              className={`filter-pill-btn ${dateRangePreset === '30d' ? 'active' : ''}`}
              onClick={() => handlePresetChange('30d')}
            >
              {t.dashboard.period30d}
            </button>
            <button
              type="button"
              className={`filter-pill-btn ${dateRangePreset === 'month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('month')}
            >
              {t.dashboard.periodMonth}
            </button>
            <button
              type="button"
              className={`filter-pill-btn ${dateRangePreset === 'year' ? 'active' : ''}`}
              onClick={() => handlePresetChange('year')}
            >
              {t.dashboard.periodYear}
            </button>
          </div>

          {/* Date Pickers Personnalisés */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="date"
              className="filter-input-date"
              value={dateDebut}
              onChange={e => {
                setDateDebut(e.target.value);
                setDateRangePreset('custom');
              }}
              title={t.dashboard.dateDebut}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>→</span>
            <input
              type="date"
              className="filter-input-date"
              value={dateFin}
              onChange={e => {
                setDateFin(e.target.value);
                setDateRangePreset('custom');
              }}
              title={t.dashboard.dateFin}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: 600 }}>Campagne:</span>
            <select 
              className="filter-input-date" 
              style={{ minWidth: 150 }} 
              value={campagneId} 
              onChange={handleCampagneChange}
            >
              <option value="all">Toutes les campagnes</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>{c.libelle}</option>
              ))}
            </select>
          </div>

          {/* Bouton de réinitialisation si filtre actif */}
          {isFilterActive && (
            <button
              className="btn btn-outline btn-sm"
              style={{ borderColor: 'var(--danger)', color: 'var(--danger)', padding: '4px 10px' }}
              onClick={handleResetFilters}
              title={t.dashboard.resetFilters}
            >
              <RotateCcw size={12} />
              {t.dashboard.resetFilters}
            </button>
          )}
        </div>
      </div>

      {/* Cartes KPI Principales */}
      <div className="grid-kpi mb-6">
        <KpiCard
          icon={TrendingUp}
          label={t.dashboard.totalSales}
          value={formatFCFA(stats?.ventes?.total_fcfa)}
          sub={`${stats?.ventes?.count || 0} ${t.stats.transactions}`}
          color="#10B981"
          bgColor="rgba(16,185,129,0.12)"
        />
        <KpiCard
          icon={ShoppingCart}
          label={t.dashboard.totalPurchases}
          value={formatFCFA(stats?.achats?.total_fcfa)}
          sub={`${stats?.achats?.count || 0} ${t.stats.transactions}`}
          color="#F59E0B"
          bgColor="rgba(245,158,11,0.12)"
        />
        <KpiCard
          icon={DollarSign}
          label={t.dashboard.totalInvestments}
          value={formatFCFA(stats?.investissements?.total_fcfa)}
          sub={`${stats?.investissements?.count || 0} ${t.stats.transactions}`}
          color="#8B5CF6"
          bgColor="rgba(139,92,246,0.12)"
        />
        <KpiCard
          icon={Megaphone}
          label={t.dashboard.totalCampaigns}
          value={stats?.campagnes?.count || 0}
          color="#14B8A6"
          bgColor="rgba(20,184,166,0.12)"
        />
        <KpiCard
          icon={Users}
          label={isInvestor ? t.dashboard.partners : t.dashboard.investors}
          value={isInvestor ? (stats?.partenaires?.count || 0) : (stats?.investisseurs?.count || 0)}
          color="#3B82F6"
          bgColor="rgba(59,130,246,0.12)"
        />
        <KpiCard
          icon={Package}
          label={t.dashboard.stockAlert}
          value={outOfStockItems.length}
          sub={outOfStockItems.length > 0 ? `${outOfStockItems.length} ${t.dashboard.stockOutOf}` : t.dashboard.stockAllAvailable}
          color={outOfStockItems.length > 0 ? '#EF4444' : '#10B981'}
          bgColor={outOfStockItems.length > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)'}
        />
      </div>

      {/* SECTION CALCULS FINANCIERS CLÉS (Revient & Différence Investissement - Achat) */}
      <div className="grid-2 mb-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))' }}>
        {/* 1. CARTE REVIENT : TOTAL VENTES - TOTAL ACHATS */}
        <div className={`financial-metric-card ${isRevientPositif ? 'positive' : 'negative'}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.25rem' }}>💰</span>
                <span style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
                  {t.dashboard.revient}
                </span>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: 4 }}>
                {t.dashboard.revientSub}
              </div>
            </div>
            <span className={`badge ${isRevientPositif ? 'badge-success' : 'badge-danger'}`} style={{ fontWeight: 600 }}>
              {isRevientPositif ? t.dashboard.marginPositive : t.dashboard.marginNegative}
            </span>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="number-format" style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: isRevientPositif ? 'var(--success)' : 'var(--danger)'
            }}>
              {isRevientPositif ? '+' : ''}{formatFCFA(revientValeur)}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
              <div className="financial-metric-formula">
                {t.dashboard.salesFormula} ({formatFCFA(totalVentes)}) − {t.dashboard.purchasesFormula} ({formatFCFA(totalAchats)})
              </div>
              {margePercent !== null && (
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: isRevientPositif ? 'var(--success)' : 'var(--danger)' }}>
                  {t.dashboard.margin}: {margePercent}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 2. CARTE DIFFÉRENCE : TOTAL INVESTISSEMENT - TOTAL ACHAT */}
        <div className={`financial-metric-card ${isDiffInvestPositif ? 'positive' : 'negative'}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Scale size={20} style={{ color: 'var(--gold)' }} />
                <span style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
                  {t.dashboard.diffInvestAchats}
                </span>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: 4 }}>
                {t.dashboard.diffInvestAchatsSub}
              </div>
            </div>
            <span className={`badge ${isDiffInvestPositif ? 'badge-gold' : 'badge-danger'}`} style={{ fontWeight: 600 }}>
              {isDiffInvestPositif ? t.dashboard.capitalAvailable : t.dashboard.budgetOverrun}
            </span>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="number-format" style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: isDiffInvestPositif ? 'var(--gold-dark, #D97706)' : 'var(--danger)'
            }}>
              {isDiffInvestPositif ? '+' : ''}{formatFCFA(diffInvestAchatsValeur)}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
              <div className="financial-metric-formula">
                {t.dashboard.investedFormula} ({formatFCFA(totalInvest)}) − {t.dashboard.purchasesFormula} ({formatFCFA(totalAchats)})
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {totalInvest > 0 ? `${((totalAchats / totalInvest) * 100).toFixed(0)}% ${t.dashboard.consumed}` : '0%'}
              </span>
            </div>
          </div>
        </div>

        {/* 3. CARTE MES REVENUS PERSONNELS (Basés sur le bénéfice) */}
        <div className={`financial-metric-card ${myIncome >= 0 ? 'positive' : 'negative'}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.25rem' }}>🎯</span>
                <span style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
                  {t.dashboard.myRevenues}
                </span>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: 4 }}>
                {t.dashboard.myRevenuesSub}
              </div>
            </div>
            <span className={`badge ${myIncome >= 0 ? 'badge-success' : 'badge-danger'}`} style={{ fontWeight: 600 }}>
              {myPercentage}{t.dashboard.myShare}
            </span>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="number-format" style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: myIncome >= 0 ? 'var(--success)' : 'var(--danger)'
            }}>
              {myIncome > 0 ? '+' : ''}{formatFCFA(myIncome)}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
              <div className="financial-metric-formula">
                {t.dashboard.profitFormula} ({formatFCFA(benefice)}) × {myPercentage}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bannière Alerte Stock : uniquement les produits épuisés */}
      {outOfStockItems.length > 0 ? (
        <div className="card mb-6" style={{
          borderLeft: '4px solid var(--danger)',
          background: 'rgba(239, 68, 68, 0.05)',
          padding: '16px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AlertTriangle size={24} color="var(--danger)" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                {t.dashboard.stockOutBanner} ({outOfStockItems.length})
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 2 }}>
                {t.dashboard.stockRefillPrompt}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {outOfStockItems.map((p, idx) => (
                <span key={idx} className="badge badge-danger">
                  📦 {p.libelle}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="card mb-6" style={{
          borderLeft: '4px solid var(--success)',
          background: 'rgba(16, 185, 129, 0.05)',
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <CheckCircle2 size={18} color="var(--success)" />
          <span style={{ fontSize: '0.875rem', color: 'var(--success)', fontWeight: 500 }}>
            {t.dashboard.stockAllGood}
          </span>
        </div>
      )}

      {/* Résultat Net Global */}
      <div className="mb-6">
        <div
          className={`profit-indicator ${isProfit ? 'positive' : 'negative'}`}
          style={{ maxWidth: 460 }}
        >
          <span className="profit-indicator-icon">
            {isProfit ? '📈' : '📉'}
          </span>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 2 }}>
              {t.dashboard.benefice} ({t.dashboard.beneficeFormula})
            </div>
            <div className={`profit-indicator-value number-format`}>
              {isProfit ? '+' : ''}{formatFCFA(benefice)}
            </div>
            <div style={{ fontSize: '0.75rem', marginTop: 2 }}>
              {isProfit
                ? <span style={{ color: 'var(--success)' }}>✓ {t.stats.profit}</span>
                : <span style={{ color: 'var(--danger)' }}>↓ {t.stats.loss}</span>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Charts d'évolution des Ventes et Achats */}
      <div className="grid-2 mb-6">
        <MiniBarChart
          data={stats?.ventes?.evolution}
          label={t.dashboard.salesEvolution}
          color="var(--primary)"
        />
        <MiniBarChart
          data={stats?.achats?.evolution}
          label={t.dashboard.purchasesEvolution || t.dashboard.purchasesEvolution}
          color="var(--gold)"
        />
      </div>

      {/* Tableau du Stock */}
      {stats?.stock && stats.stock.length > 0 && (
        <div className="card mb-6">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Package size={18} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>{t.dashboard.stockManagement}</span>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t.dashboard.colProduct}</th>
                  <th>{t.dashboard.colQty}</th>
                  <th>{t.dashboard.colSalePrice}</th>
                  <th>{t.dashboard.colStockValue}</th>
                  <th>{t.dashboard.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                {stats.stock.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{p.libelle}</strong></td>
                    <td className="number-format">{p.quantite}</td>
                    <td className="number-format">{formatFCFA(p.prix_unitaire_vente)}</td>
                    <td className="number-format">{formatFCFA(p.valeur_stock)}</td>
                    <td>
                      {p.quantite === 0
                        ? <span className="badge badge-danger">{t.dashboard.stockStatusOut}</span>
                        : p.quantite < 5
                          ? <span className="badge badge-gold">{t.dashboard.stockStatusLow}</span>
                          : <span className="badge badge-success">{t.dashboard.stockStatusOk}</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bénéfice par Campagne avec Répartition des Parts */}
      {beneficeParCampagne.length > 0 && (
        <div className="card mb-6">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.2rem' }}>📊</span>
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>{t.dashboard.beneficeByCampaign}</span>
            </div>
            {/* Indicateur somme des % */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 20,
              background: totalPercentAlloue === 100
                ? 'rgba(16,185,129,0.12)'
                : totalPercentAlloue === 0 ? 'rgba(100,100,100,0.08)'
                : 'rgba(239,68,68,0.12)',
              border: `1px solid ${
                totalPercentAlloue === 100 ? 'rgba(16,185,129,0.3)'
                : totalPercentAlloue === 0 ? 'rgba(100,100,100,0.15)'
                : 'rgba(239,68,68,0.3)'
              }`,
            }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color:
                totalPercentAlloue === 100 ? 'var(--success)'
                : totalPercentAlloue === 0 ? 'var(--text-muted)'
                : 'var(--danger)'
              }}>
                {t.dashboard.sharesTotal} : {totalPercentAlloue}%
              </span>
              {totalPercentAlloue === 100
                ? <span style={{ fontSize: '0.75rem', color: 'var(--success)' }}>{t.dashboard.sharesBalanced}</span>
                : totalPercentAlloue > 0 && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>
                    {totalPercentAlloue > 100 ? `▲ +${totalPercentAlloue - 100}%` : `▼ −${100 - totalPercentAlloue}%`} {t.dashboard.sharesAbove}
                  </span>
                )
              }
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {beneficeParCampagne.map((camp) => (
              <div key={camp.id} style={{
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                overflow: 'hidden'
              }}>
                {/* En-tête campagne */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px',
                  background: camp.benefice >= 0 ? 'rgba(16,185,129,0.06)' : 'rgba(239,68,68,0.06)',
                  borderBottom: '1px solid var(--border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: camp.benefice >= 0 ? 'var(--success)' : 'var(--danger)',
                      display: 'inline-block'
                    }} />
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{camp.libelle}</span>
                    <span className={`badge ${camp.statut === 'active' ? 'badge-primary' : 'badge-gold'}`} style={{ fontSize: '0.7rem' }}>
                      {/* {camp.statut} */}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 20, fontSize: '0.85rem', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{t.dashboard.salesFormula} : <strong style={{ color: 'var(--success)' }}>{formatFCFA(camp.total_ventes)}</strong></span>
                    <span style={{ color: 'var(--text-muted)' }}>{t.dashboard.investedFormula} : <strong style={{ color: 'var(--gold)' }}>{formatFCFA(camp.total_investissements)}</strong></span>
                    <span style={{ color: 'var(--text-muted)' }}>{t.dashboard.benefice} : <strong style={{ color: camp.benefice >= 0 ? 'var(--success)' : 'var(--danger)', fontSize: '1rem' }}>
                      {camp.benefice >= 0 ? '+' : ''}{formatFCFA(camp.benefice)}
                    </strong></span>
                  </div>
                </div>

                {/* Répartition des membres */}
                {camp.repartition && camp.repartition.filter(m => m.percentage > 0).length > 0 ? (
                  <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>{t.dashboard.sharesBreakdown}</div>
                    {camp.repartition.filter(m => m.percentage > 0).map((member) => (
                      <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: member.role === 'investisseur'
                            ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
                            : 'linear-gradient(135deg, var(--primary), var(--gold))',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: '0.75rem', color: 'white'
                        }}>
                          {(member.prenom?.[0] || '?')}{(member.nom?.[0] || '')}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                              {member.prenom} {member.nom}
                              <span style={{
                                marginLeft: 6, fontSize: '0.7rem', fontWeight: 500,
                                color: member.role === 'investisseur' ? '#8B5CF6' : 'var(--primary)'
                              }}>
                                ({member.role})
                              </span>
                            </span>
                            <div style={{ display: 'flex', gap: 12, fontSize: '0.85rem', alignItems: 'center' }}>
                              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{member.percentage}%</span>
                              <span style={{
                                fontWeight: 700,
                                color: member.income >= 0 ? 'var(--success)' : 'var(--danger)'
                              }}>
                                {member.income >= 0 ? '+' : ''}{formatFCFA(member.income)}
                              </span>
                            </div>
                          </div>
                          <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${Math.min(member.percentage, 100)}%`,
                              background: member.role === 'investisseur'
                                ? 'linear-gradient(90deg, #8B5CF6, #6D28D9)'
                                : 'linear-gradient(90deg, var(--primary), var(--primary-dark))',
                              borderRadius: 3, transition: 'width 0.5s ease'
                            }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    {t.dashboard.noShareAssigned}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Investissements par campagne (investisseur) */}
      {isInvestor && stats?.investissements?.details && stats.investissements.details.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <DollarSign size={18} style={{ color: 'var(--gold)' }} />
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>{t.dashboard.myInvestmentsByCampaign}</span>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t.dashboard.colCampaign}</th>
                  <th>{t.dashboard.colAmount}</th>
                  <th>{t.dashboard.colDate}</th>
                  <th>{t.dashboard.colShare}</th>
                </tr>
              </thead>
              <tbody>
                {stats.investissements.details.map((inv, i) => (
                  <tr key={i}>
                    <td>{inv.campagne_libelle || inv.campagne_id}</td>
                    <td className="number-format">{formatFCFA(inv.montant)}</td>
                    <td>{inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          height: 6, flex: 1, background: 'var(--border)', borderRadius: 3, overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${inv.pourcentage}%`,
                            background: 'linear-gradient(90deg, var(--primary), var(--primary-dark))',
                            borderRadius: 3,
                          }} />
                        </div>
                        <span className="number-format" style={{ minWidth: 40, textAlign: 'right', fontWeight: 600 }}>
                          {inv.pourcentage}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
